import { z } from 'zod';
import { difficultySchema, lessonStatusSchema } from '../enums.js';
import { slugSchema } from './common.js';

/**
 * Curriculum contracts.
 *
 * Note what is absent: `solutionSql` never appears in any response schema. The
 * reference solution stays server-side, because a learner who can read it from
 * a network response is not learning anything.
 */

export const lessonSummarySchema = z.object({
  slug: z.string(),
  title: z.string(),
  estimatedMinutes: z.number().int(),
  xpReward: z.number().int(),
  exerciseCount: z.number().int(),
  status: lessonStatusSchema,
});
export type LessonSummary = z.infer<typeof lessonSummarySchema>;

export const moduleSummarySchema = z.object({
  slug: z.string(),
  title: z.string(),
  summaryMd: z.string(),
  level: z.number().int(),
  lessons: z.array(lessonSummarySchema),
});
export type ModuleSummary = z.infer<typeof moduleSummarySchema>;

export const courseDetailSchema = z.object({
  slug: z.string(),
  title: z.string(),
  descriptionMd: z.string(),
  modules: z.array(moduleSummarySchema),
  /** Progress across the whole course, for the header. */
  totals: z.object({
    lessons: z.number().int(),
    lessonsCompleted: z.number().int(),
    exercises: z.number().int(),
    exercisesSolved: z.number().int(),
  }),
});
export type CourseDetail = z.infer<typeof courseDetailSchema>;

/** An exercise as the learner sees it — no solution, no hint bodies. */
export const exercisePublicSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  difficulty: difficultySchema,
  promptMd: z.string(),
  starterSql: z.string(),
  datasetSlug: z.string(),
  tags: z.array(z.string()),
  xpReward: z.number().int(),
  hintCount: z.number().int(),
  /** How many hints this learner has already revealed. */
  hintsRevealed: z.number().int(),
  solved: z.boolean(),
});
export type ExercisePublic = z.infer<typeof exercisePublicSchema>;

export const lessonDetailSchema = z.object({
  slug: z.string(),
  title: z.string(),
  contentMd: z.string(),
  estimatedMinutes: z.number().int(),
  xpReward: z.number().int(),
  status: lessonStatusSchema,
  module: z.object({ slug: z.string(), title: z.string(), level: z.number().int() }),
  /** Position within the module, for "Lesson 5 of 8" and prev/next links. */
  position: z.object({
    index: z.number().int(),
    total: z.number().int(),
    previousSlug: z.string().nullable(),
    nextSlug: z.string().nullable(),
  }),
  exercises: z.array(exercisePublicSchema),
});
export type LessonDetail = z.infer<typeof lessonDetailSchema>;

export const lessonParamsSchema = z.object({ slug: slugSchema });
export const courseParamsSchema = z.object({ slug: slugSchema });
